import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import RuleFilters from './components/RuleFilters';
import RulesTable from './components/RulesTable';
import RuleStatsPanel from './components/RuleStatsPanel';
import RuleEditorModal from './components/RuleEditorModal';

const RegulatoryRulesLibrary = () => {
  const [filters, setFilters] = useState({
    search: '',
    jurisdiction: 'all',
    category: 'all',
    severity: 'all',
    status: 'all',
    updatedAfter: '',
    updatedBefore: ''
  });

  const [selectedRules, setSelectedRules] = useState([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // Mock data for regulatory rules
  const mockRules = [
    {
      id: 'rule-001',
      name: 'FDIC Deposit Insurance Disclosure',
      jurisdiction: 'fdic',
      category: 'disclosure',
      severity: 'critical',
      status: 'active',
      description: 'Required disclosure for FDIC deposit insurance coverage in marketing materials',
      regulatoryText: `All marketing materials that reference deposit accounts must include clear disclosure that deposits are insured by the FDIC up to the maximum amount allowed by law. The disclosure must be prominently displayed and easily readable.`,
      aiParameters: {
        keywords: ['deposit', 'FDIC', 'insured', 'coverage'],
        contextWeight: 0.9,
        strictMode: true,
        autoFlag: true
      },
      citations: ['12 CFR 328.3', 'FDIC FIL-37-2001'],
      effectiveDate: '2023-01-01',
      expiryDate: null,
      lastUpdated: '2025-09-15',
      createdAt: '2023-01-01',
      violations: 89,
      monthlyViolations: 23,
      avgSeverity: 'high'
    },
    {
      id: 'rule-002',
      name: 'Truth in Lending Act Compliance',
      jurisdiction: 'federal',
      category: 'lending',
      severity: 'high',
      status: 'active',
      description: 'APR disclosure requirements for credit product advertisements',
      regulatoryText: `Credit advertisements must clearly disclose the Annual Percentage Rate (APR) and any material terms. If specific rates are mentioned, all applicable rates and conditions must be disclosed.`,
      aiParameters: {
        keywords: ['APR', 'interest rate', 'credit', 'loan'],
        contextWeight: 0.8,
        strictMode: true,
        autoFlag: true
      },
      citations: ['15 USC 1601', 'Regulation Z'],
      effectiveDate: '2023-01-01',
      expiryDate: null,
      lastUpdated: '2025-09-10',
      createdAt: '2023-01-01',
      violations: 67,
      monthlyViolations: 18,
      avgSeverity: 'medium'
    },
    {
      id: 'rule-003',
      name: 'Fair Credit Reporting Act',
      jurisdiction: 'federal',
      category: 'consumer-protection',
      severity: 'high',
      status: 'active',
      description: 'Consumer rights disclosure for credit-related services',
      regulatoryText: `Marketing materials for credit monitoring or credit-related services must include appropriate disclosures about consumer rights under the Fair Credit Reporting Act.`,
      aiParameters: {
        keywords: ['credit report', 'credit score', 'credit monitoring'],
        contextWeight: 0.7,
        strictMode: false,
        autoFlag: true
      },
      citations: ['15 USC 1681'],
      effectiveDate: '2023-01-01',
      expiryDate: null,
      lastUpdated: '2025-09-08',
      createdAt: '2023-01-01',
      violations: 45,
      monthlyViolations: 12,
      avgSeverity: 'medium'
    },
    {
      id: 'rule-004',
      name: 'Consumer Privacy Rights',
      jurisdiction: 'cfpb',
      category: 'privacy',
      severity: 'medium',
      status: 'active',
      description: 'Privacy policy disclosure requirements for financial services',
      regulatoryText: `Financial institutions must provide clear notice of their privacy practices and consumers' rights regarding personal information sharing.`,
      aiParameters: {
        keywords: ['privacy', 'personal information', 'data sharing'],
        contextWeight: 0.6,
        strictMode: false,
        autoFlag: false
      },
      citations: ['GLBA Privacy Rule'],
      effectiveDate: '2023-01-01',
      expiryDate: null,
      lastUpdated: '2025-09-05',
      createdAt: '2023-01-01',
      violations: 32,
      monthlyViolations: 8,
      avgSeverity: 'low'
    },
    {
      id: 'rule-005',
      name: 'Anti-Money Laundering Compliance',
      jurisdiction: 'federal',
      category: 'aml',
      severity: 'critical',
      status: 'active',
      description: 'AML compliance requirements for financial product marketing',
      regulatoryText: `Marketing materials for financial products must not facilitate or encourage activities that could be used for money laundering or terrorist financing.`,
      aiParameters: {
        keywords: ['cash transactions', 'anonymous', 'no questions asked'],
        contextWeight: 0.9,
        strictMode: true,
        autoFlag: true
      },
      citations: ['Bank Secrecy Act', '31 USC 5311'],
      effectiveDate: '2023-01-01',
      expiryDate: null,
      lastUpdated: '2025-09-03',
      createdAt: '2023-01-01',
      violations: 28,
      monthlyViolations: 7,
      avgSeverity: 'high'
    },
    {
      id: 'rule-006',
      name: 'Equal Credit Opportunity Act',
      jurisdiction: 'federal',
      category: 'fair-lending',
      severity: 'high',
      status: 'active',
      description: 'Prohibited discriminatory language in credit advertising',
      regulatoryText: `Credit advertisements must not contain language that discourages applications from protected classes or suggests discriminatory lending practices.`,
      aiParameters: {
        keywords: ['preferred customers', 'select clientele', 'exclusive'],
        contextWeight: 0.8,
        strictMode: true,
        autoFlag: true
      },
      citations: ['15 USC 1691', 'Regulation B'],
      effectiveDate: '2023-01-01',
      expiryDate: null,
      lastUpdated: '2025-09-01',
      createdAt: '2023-01-01',
      violations: 19,
      monthlyViolations: 5,
      avgSeverity: 'medium'
    },
    {
      id: 'rule-007',
      name: 'SEC Investment Adviser Marketing',
      jurisdiction: 'sec',
      category: 'advertising',
      severity: 'medium',
      status: 'pending',
      description: 'Marketing rule compliance for investment advisers',
      regulatoryText: `Investment adviser marketing materials must be fair and balanced, include required disclosures, and not contain misleading statements about performance or services.`,
      aiParameters: {
        keywords: ['guaranteed returns', 'risk-free', 'best performance'],
        contextWeight: 0.7,
        strictMode: false,
        autoFlag: false
      },
      citations: ['Investment Advisers Act Rule 206(4)-1'],
      effectiveDate: '2023-05-01',
      expiryDate: null,
      lastUpdated: '2025-08-28',
      createdAt: '2023-05-01',
      violations: 15,
      monthlyViolations: 3,
      avgSeverity: 'low'
    },
    {
      id: 'rule-008',
      name: 'FINRA Social Media Guidelines',
      jurisdiction: 'finra',
      category: 'advertising',
      severity: 'medium',
      status: 'active',
      description: 'Social media compliance for broker-dealers',
      regulatoryText: `Social media communications by broker-dealers must comply with advertising rules, include appropriate disclaimers, and be supervised according to firm policies.`,
      aiParameters: {
        keywords: ['social media', 'investment advice', 'recommendations'],
        contextWeight: 0.6,
        strictMode: false,
        autoFlag: false
      },
      citations: ['FINRA Rule 2210'],
      effectiveDate: '2023-01-01',
      expiryDate: null,
      lastUpdated: '2025-08-25',
      createdAt: '2023-01-01',
      violations: 11,
      monthlyViolations: 2,
      avgSeverity: 'low'
    }
  ];

  const [rules, setRules] = useState(mockRules);
  const [filteredRules, setFilteredRules] = useState(mockRules);

  // Current user mock data
  const currentUser = {
    name: "Sarah Chen",
    role: "Compliance Manager"
  };

  // Badge counts for header
  const badgeCounts = {
    dashboard: 12,
    review: 8
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/content-analysis-dashboard' },
    { label: 'Rules Library' }
  ];

  // Filter rules based on current filters
  useEffect(() => {
    let filtered = rules;

    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(rule =>
        rule?.name?.toLowerCase()?.includes(searchTerm) ||
        rule?.description?.toLowerCase()?.includes(searchTerm) ||
        rule?.regulatoryText?.toLowerCase()?.includes(searchTerm)
      );
    }

    if (filters?.jurisdiction !== 'all') {
      filtered = filtered?.filter(rule => rule?.jurisdiction === filters?.jurisdiction);
    }

    if (filters?.category !== 'all') {
      filtered = filtered?.filter(rule => rule?.category === filters?.category);
    }

    if (filters?.severity !== 'all') {
      filtered = filtered?.filter(rule => rule?.severity === filters?.severity);
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(rule => rule?.status === filters?.status);
    }

    if (filters?.updatedAfter) {
      filtered = filtered?.filter(rule => 
        new Date(rule.lastUpdated) >= new Date(filters.updatedAfter)
      );
    }

    if (filters?.updatedBefore) {
      filtered = filtered?.filter(rule => 
        new Date(rule.lastUpdated) <= new Date(filters.updatedBefore)
      );
    }

    setFilteredRules(filtered);
  }, [filters, rules]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      jurisdiction: 'all',
      category: 'all',
      severity: 'all',
      status: 'all',
      updatedAfter: '',
      updatedBefore: ''
    });
  };

  const handleSelectRule = (ruleId, isSelected) => {
    setSelectedRules(prev => 
      isSelected 
        ? [...prev, ruleId]
        : prev?.filter(id => id !== ruleId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedRules(isSelected ? filteredRules?.map(rule => rule?.id) : []);
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setIsEditorOpen(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setIsEditorOpen(true);
  };

  const handleDuplicateRule = (rule) => {
    const duplicatedRule = {
      ...rule,
      id: `rule-${Date.now()}`,
      name: `${rule?.name} (Copy)`,
      status: 'inactive',
      createdAt: new Date()?.toISOString()?.split('T')?.[0],
      lastUpdated: new Date()?.toISOString()?.split('T')?.[0],
      violations: 0,
      monthlyViolations: 0
    };
    setRules(prev => [...prev, duplicatedRule]);
  };

  const handleToggleRuleStatus = (ruleId) => {
    setRules(prev => prev?.map(rule => 
      rule?.id === ruleId 
        ? { 
            ...rule, 
            status: rule?.status === 'active' ? 'inactive' : 'active',
            lastUpdated: new Date()?.toISOString()?.split('T')?.[0]
          }
        : rule
    ));
  };

  const handleSaveRule = async (ruleData) => {
    if (editingRule) {
      // Update existing rule
      setRules(prev => prev?.map(rule => 
        rule?.id === editingRule?.id 
          ? { 
              ...rule, 
              ...ruleData,
              lastUpdated: new Date()?.toISOString()?.split('T')?.[0]
            }
          : rule
      ));
    } else {
      // Create new rule
      const newRule = {
        ...ruleData,
        id: `rule-${Date.now()}`,
        createdAt: new Date()?.toISOString()?.split('T')?.[0],
        lastUpdated: new Date()?.toISOString()?.split('T')?.[0],
        violations: 0,
        monthlyViolations: 0,
        avgSeverity: 'low'
      };
      setRules(prev => [...prev, newRule]);
    }
  };

  const handleBulkEdit = () => {
    if (selectedRules?.length === 0) {
      alert('Please select rules to edit');
      return;
    }
    // Implement bulk edit functionality
    console.log('Bulk editing rules:', selectedRules);
  };

  const handleImportExport = () => {
    // Implement import/export functionality
    console.log('Import/Export functionality');
  };

  const handleViewDetails = (rule) => {
    // Implement view details functionality
    console.log('Viewing rule details:', rule);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header badgeCounts={badgeCounts} currentUser={currentUser} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <Breadcrumb items={breadcrumbItems} className="mb-6" />
          
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content Area */}
            <div className="xl:col-span-3 space-y-6">
              {/* Filters */}
              <RuleFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                resultsCount={filteredRules?.length}
                onAddRule={handleAddRule}
                onBulkEdit={handleBulkEdit}
                onImportExport={handleImportExport}
              />
              
              {/* Rules Table */}
              <RulesTable
                rules={filteredRules}
                selectedRules={selectedRules}
                onSelectRule={handleSelectRule}
                onSelectAll={handleSelectAll}
                onEditRule={handleEditRule}
                onDuplicateRule={handleDuplicateRule}
                onToggleRuleStatus={handleToggleRuleStatus}
                onViewDetails={handleViewDetails}
              />
            </div>
            
            {/* Stats Panel */}
            <div className="xl:col-span-1">
              <RuleStatsPanel />
            </div>
          </div>
        </div>
      </main>
      {/* Rule Editor Modal */}
      <RuleEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        rule={editingRule}
        onSave={handleSaveRule}
      />
    </div>
  );
};

export default RegulatoryRulesLibrary;